import type {
	AttendeeOrderData,
	AttendeeCheckInResponse,
	AttendeeMessageResponse,
	AttendeeReportResponse,
	CancelItem,
	AttendeeQrCheckInInfo,
	AttendeeMessageInfo,
	OrderResponse,
	AttendeeDataResponse,
} from '@/types/attendee';

interface AttendeeStore {
	attendee: {
		orderData: AttendeeOrderData | null;
		attendeeData: AttendeeDataResponse | null;
		errorMessage: string;
	};
	checkin: {
		data: AttendeeCheckInResponse | null;
		errorMessage: string;
	};
	qrCheckIn: {
		data: AttendeeCheckInResponse | null;
		errorMessage: string;
	};
	message: {
		data: AttendeeMessageResponse | null;
		errorMessage: string;
	};
	cancel: {
		errorMessage: string;
	};
	download: {
		data: AttendeeReportResponse | null;
		errorMessage: string;
	};
	order: {
		data: OrderResponse | null;
		errorMessage: string;
	};
	isLoading: boolean;
}

const useAttendeeStore = defineStore('attendee', {
	state: (): AttendeeStore => ({
		attendee: {
			orderData: null,
			attendeeData: null,
			errorMessage: '',
		},
		checkin: {
			data: null,
			errorMessage: '',
		},
		qrCheckIn: {
			data: null,
			errorMessage: '',
		},
		message: {
			data: null,
			errorMessage: '',
		},
		cancel: {
			errorMessage: '',
		},
		download: {
			data: null,
			errorMessage: '',
		},
		order: {
			data: null,
			errorMessage: '',
		},
		isLoading: false,
	}),

	getters: {
		orderData: (state): OrderResponse | null => {
			if (!state.order.data) {
				return null;
			}
			return state.order.data;
		},

		attendeeData: (state): AttendeeOrderData | null => {
			if (!state.attendee.orderData) {
				return null;
			}
			return state.attendee.orderData;
		},

		currentPage: (state): number | null => {
			if (!state.attendee.orderData) {
				return null;
			}
			return state.attendee.orderData.paginationInfo.currentPage;
		},

		attendeeStatusCount: (state) => {
			if (!state.attendee.orderData) {
				return null;
			}
			return state.attendee.orderData.attendanceSummary;
		},

		attendeeQrCheckInTicketIds: (state): string => {
			if (!state.qrCheckIn.data) {
				return '';
			}

			return state.qrCheckIn.data.attendees.map((attendee) => attendee.id).join(',');
		},

		attendeeQrCheckInData: (state): AttendeeQrCheckInInfo | null => {
			if (!state.qrCheckIn.data) {
				return null;
			}

			return transformAttendeeQrCheckInInfo(state.qrCheckIn.data);
		},

		attendeeInfo: (state): string[] | null => {
			if (!state.download.data) {
				return null;
			}
			return transformAttendeeInfo(state.download.data);
		},

		isDownloadAttendeeInfoFromEmail: (state): boolean => {
			if (!state.download.data) {
				return false;
			}

			return state.download.data.isSendEmail;
		},

		orderTicketIds: (state): string => {
			if (!state.order.data) {
				return '';
			}
			const ids = state.order.data?.attendances.map((item) => item.id).join(',');
			return ids;
		},
	},

	actions: {
		async retrieveAttendeeOrderData(
			eventId: number,
			currentPage: number,
			searchValue?: string,
			status: string = '',
			sortBy: string = ATTENDEE_SORTING_METHOD.LATEST_ADDED,
			itemPerPage: number = PAGINATION_CONFIG.ITEM_PER_PAGE,
			searchKey: string = ATTENDEE_SEARCH_KEY.NAME,
		) {
			this.isLoading = true;
			this.attendee.errorMessage = '';

			const search = searchValue ? `${searchKey}:${searchValue}` : undefined;

			const { data, errorMessage } = await getAttendeeOrderByEventId(eventId, {
				search,
				status,
				sortBy,
				currentPage,
				itemPerPage,
			});

			if (errorMessage.value) {
				this.attendee.errorMessage = errorMessage.value;
			}

			if (data.value) {
				this.attendee.orderData = data.value;
			}
			this.isLoading = false;
		},

		async updateTicketStatus(
			eventId: number,
			attendeeId: number,
			status: string = ATTENDEE_CHECK_IN_STATUS.CHECK_IN,
		) {
			this.isLoading = true;
			this.checkin.data = null;
			this.checkin.errorMessage = '';

			const { data, errorMessage } = await putAttendeeCheckIn(eventId, {
				attendeeId,
				status,
			});

			if (errorMessage.value) {
				this.checkin.errorMessage = errorMessage.value;
			}

			if (data.value) {
				this.checkin.data = data.value.data;
			}
			this.isLoading = false;
		},

		async checkInWithQrCodeTicket(eventId: number, hash: string) {
			this.qrCheckIn.data = null;
			this.qrCheckIn.errorMessage = '';

			const { data, errorMessage } = await putAttendeeQrCodeCheckIn(eventId, hash);

			if (errorMessage.value) {
				this.qrCheckIn.errorMessage = errorMessage.value;
			}

			if (data.value) {
				this.qrCheckIn.data = data.value.data;
			}
		},

		async sendMessageToEventFollowers(eventId: number, info: AttendeeMessageInfo) {
			this.isLoading = true;
			this.message.data = null;
			this.message.errorMessage = '';

			if (info.type === ATTENDEE_MESSAGE_TYPE.TO_ALL) {
				const { data, errorMessage } = await postAttendeeMessageToAll(eventId, {
					type: ATTENDEE_MESSAGE_TYPE.TO_ALL, // default for send message to all is 2
					body: info.body,
					subject: info.subject,
				});

				if (errorMessage.value) {
					this.message.errorMessage = errorMessage.value;
				}

				if (data.value) {
					this.message.data = data.value.data;
				}
			} else if (info.type === ATTENDEE_MESSAGE_TYPE.TO_SPECIFIC) {
				const { errorMessage } = await postAttendeeMessageToIndividual(eventId, {
					ids: info.ids,
					body: info.body,
					subject: info.subject,
				});

				if (errorMessage.value) {
					this.message.errorMessage = errorMessage.value;
				}
			}

			this.isLoading = false;
		},

		async cancelOrder(eventId: number, saleId: number, items: CancelItem[]) {
			this.isLoading = true;
			this.cancel.errorMessage = '';

			const { errorMessage } = await postAttendeeCancel(eventId, saleId, items);

			if (errorMessage.value) {
				this.cancel.errorMessage = errorMessage.value;
			}

			this.isLoading = false;
		},

		async downloadInfo(eventId: number) {
			this.isLoading = true;
			this.download.data = null;
			this.download.errorMessage = '';

			const { data, errorMessage } = await getAttendeeDownloadByEventId(eventId);

			if (errorMessage.value) {
				this.download.errorMessage = errorMessage.value;
			}

			if (data.value) {
				this.download.data = data.value;
			}
			this.isLoading = false;
		},

		async getOrder(eventId: number, salesId: number) {
			this.isLoading = true;
			this.order.errorMessage = '';

			const { data, errorMessage } = await getOrderBySalesId(eventId, salesId);

			if (errorMessage.value) {
				this.order.errorMessage = errorMessage.value;
			}

			if (data.value) {
				this.order.data = data.value;
			}
			this.isLoading = false;
		},

		async retrieveAttendees(eventId: number, currentPage: number = 1, limit: number = PAGINATION_CONFIG.ITEM_PER_SMALL_PAGE) {
			this.isLoading = true;
			this.attendee.errorMessage = '';

			const { data, errorMessage } = await getAttendeesByEventId(eventId, currentPage, limit);

			if (errorMessage.value) {
				this.attendee.errorMessage = errorMessage.value;
			}

			if (data.value) {
				this.attendee.attendeeData = data.value;
			}
			this.isLoading = false;
		},

		async retrieveAttendeeFile(eventId: number, key: string) {
			this.isLoading = true;
			this.attendee.errorMessage = '';

			const { data, errorMessage } = await getAttendeeFileByEventId(eventId, key);

			if (errorMessage.value) {
				this.attendee.errorMessage = errorMessage.value;
				this.isLoading = false;
				return null;
			}

			this.isLoading = false;
			return data.value ?? null;
		},
	},
});

export default useAttendeeStore;